/*
  Persist the buyer's checkout-step entries in browser sessionStorage so
  the payment step can re-emit them as form fields at submit time.

  Why not server-side context.session?  This MIG instance silently drops
  every `{% session %}` write that happens inside a `format: json` API
  endpoint (verified empirically — even the addon's own /api/create-company
  fails to persist `session company_uuid`). The server-side
  /api/save-checkout-session response says `{"saved":true}` but the
  session map remains untouched on the next request, so the addon's
  payment form's `default_payload` (which reads from context.session)
  ends up with empty strings everywhere.

  Workaround: keep the canonical step output in sessionStorage and have
  CheckoutPayment write the values onto the addon form's hidden inputs
  immediately before submit. The payment form itself is unchanged — it
  still receives the same property names PlatformOS expects, just sourced
  from the browser instead of the platform session.

  The confirmation page (/confirmation) clears these keys via
  clearCheckoutDrafts() on mount so the next checkout starts clean.
*/

const PREFIX = 'mig-checkout'

export const CHECKOUT_KEYS = {
  contact: `${PREFIX}-contact`,
  shipping: `${PREFIX}-shipping`,
  billing: `${PREFIX}-billing`,
  /** Remembers the most-recent CRM address record we created via
   *  /api/create-contact-address during this checkout, per label. Used
   *  to dedupe back-and-forth Continue clicks on /checkout/shipping
   *  and /checkout/billing — if the user's typed fields exactly match
   *  this stored record, we reuse its id/uuid instead of POSTing
   *  again. Lives ONLY in the browser; cleared with clearAll() on
   *  order success. */
  createdAddressShipping: `${PREFIX}-created-address-shipping`,
  createdAddressBilling: `${PREFIX}-created-address-billing`,
} as const

export interface CreatedAddressRecord {
  id: string
  uuid: string
  address_label: string
  address_1: string
  address_2: string
  suburb: string
  /** Stored alongside the dedupe-relevant fields so the synthesized
   *  saved-address card on revisit carries the original state through
   *  to syncCheckoutSession. Dedupe matching intentionally ignores
   *  `state` (NSW vs "New South Wales" should still collapse). */
  state: string
  postcode: string
  country: string
}

export interface CheckoutContactData {
  contact_company_name: string
  contact_first_name: string
  contact_last_name: string
  contact_email: string
  contact_phone_number: string
  contact_phone_country_code: string
  /** CRM contact UUID resolved on the contact step (createContact /
   *  updateContact response, or `payload.contact_uuid` for authenticated
   *  buyers). Carried forward to the shipping/billing steps so they can
   *  attach addresses to the right contact even when the server-side
   *  session.guest_uuid write doesn't persist. */
  resolved_contact_uuid?: string | null
}

export interface CheckoutShippingData {
  /** Chosen shipping option key (UAT-36) — one of the ShippingOptionKey
   *  values from src/lib/ecommerce/shippingOptions.ts, or '' before a
   *  selection. Authoritative source the payment-step mirror reads to
   *  set shipping_option + freight fields on the order. */
  shipping_option: string
  shipping_same_with_account: boolean
  shipping_company_name: string
  shipping_contact_first_name: string
  shipping_contact_last_name: string
  shipping_contact_email: string
  shipping_contact_phone_number: string
  shipping_contact_phone_country_code: string
  shipping_address_id: string
  shipping_address_1: string
  shipping_address_2: string
  shipping_address_3: string
  shipping_suburb: string
  shipping_state: string
  shipping_postcode: string
  shipping_country: string
  shipping_instructions: string
}

export interface CheckoutBillingData {
  billing_same_with_shipping: boolean
  billing_company_name: string
  billing_contact_first_name: string
  billing_contact_last_name: string
  billing_contact_email: string
  billing_contact_phone_number: string
  billing_contact_phone_country_code: string
  billing_address_id: string
  billing_address_1: string
  billing_address_2: string
  billing_address_3: string
  billing_suburb: string
  billing_state: string
  billing_postcode: string
  billing_country: string
}

/** Did the browser arrive at this page via back / forward history
 *  traversal? Used by the checkout-step state initializers to decide
 *  whether to rehydrate their per-keystroke `*-draft` sessionStorage
 *  entries (restore the buyer's typed values) or to start from the
 *  payload defaults (fresh visit or refresh). Modern browsers populate
 *  `PerformanceNavigationTiming.type === 'back_forward'` for any
 *  history-driven navigation; everything else (link click, manual
 *  refresh, opening the URL in a new tab) reports 'navigate' or
 *  'reload'. */
export function isBackForwardNavigation(): boolean {
  if (typeof window === 'undefined') return false
  try {
    const entries = window.performance?.getEntriesByType?.('navigation')
    if (!entries || entries.length === 0) return false
    const entry = entries[0] as PerformanceNavigationTiming
    return entry.type === 'back_forward'
  } catch {
    return false
  }
}

/* Flag used to make the in-page checkout BACK link behave like the
   browser back button. A plain `<a href="/checkout/shipping">BACK</a>`
   would land on the destination as a 'navigate' entry — so the
   destination's state-init would clear its draft (treat the visit as
   fresh entry) instead of restoring the buyer's typed values.

   To mimic the browser-back restore behaviour, the BACK link sets
   this flag on left-click; the destination's state-init reads it
   alongside `isBackForwardNavigation()` and, if either is true,
   rehydrates the draft. The flag is one-shot — consumed at the
   destination so a subsequent unrelated fresh navigation isn't
   misclassified. */
const INTERNAL_BACK_FLAG = 'mig-checkout-internal-back'

export function markInternalCheckoutBack(): void {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.setItem(INTERNAL_BACK_FLAG, '1')
  } catch {
    // sessionStorage can throw in private windows / quota — silent.
  }
}

export function isInternalCheckoutBack(): boolean {
  if (typeof window === 'undefined') return false
  try {
    return window.sessionStorage.getItem(INTERNAL_BACK_FLAG) === '1'
  } catch {
    return false
  }
}

export function consumeInternalCheckoutBack(): void {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.removeItem(INTERNAL_BACK_FLAG)
  } catch {
    // ignore
  }
}

function safeGet<T>(key: string): T | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.sessionStorage.getItem(key)
    if (!raw) return null
    return JSON.parse(raw) as T
  } catch {
    return null
  }
}

function safeSet(key: string, value: unknown): void {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.setItem(key, JSON.stringify(value))
  } catch {
    // sessionStorage may throw in private windows / when quota is hit.
  }
}

function safeRemove(key: string): void {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.removeItem(key)
  } catch {
    // ignore
  }
}

export const checkoutStorage = {
  saveContact(data: CheckoutContactData): void {
    safeSet(CHECKOUT_KEYS.contact, data)
  },
  saveShipping(data: CheckoutShippingData): void {
    safeSet(CHECKOUT_KEYS.shipping, data)
  },
  saveBilling(data: CheckoutBillingData): void {
    safeSet(CHECKOUT_KEYS.billing, data)
  },
  readContact(): CheckoutContactData | null {
    return safeGet<CheckoutContactData>(CHECKOUT_KEYS.contact)
  },
  readShipping(): CheckoutShippingData | null {
    return safeGet<CheckoutShippingData>(CHECKOUT_KEYS.shipping)
  },
  readBilling(): CheckoutBillingData | null {
    return safeGet<CheckoutBillingData>(CHECKOUT_KEYS.billing)
  },
  /** Read the most-recently-created CRM address record for this
   *  label (Shipping or Billing) during the current checkout. Returns
   *  null if nothing has been created yet this session. */
  readCreatedAddress(label: 'Shipping' | 'Billing'): CreatedAddressRecord | null {
    const key =
      label === 'Shipping'
        ? CHECKOUT_KEYS.createdAddressShipping
        : CHECKOUT_KEYS.createdAddressBilling
    return safeGet<CreatedAddressRecord>(key)
  },
  /** Remember the address record we just created, so a back-nav +
   *  re-submit on the same step can short-circuit before POSTing. */
  saveCreatedAddress(label: 'Shipping' | 'Billing', record: CreatedAddressRecord): void {
    const key =
      label === 'Shipping'
        ? CHECKOUT_KEYS.createdAddressShipping
        : CHECKOUT_KEYS.createdAddressBilling
    safeSet(key, record)
  },
  /** Drop all step data — call once an order is placed so the next
   *  checkout starts from a clean slate. */
  clearAll(): void {
    safeRemove(CHECKOUT_KEYS.contact)
    safeRemove(CHECKOUT_KEYS.shipping)
    safeRemove(CHECKOUT_KEYS.billing)
    safeRemove(CHECKOUT_KEYS.createdAddressShipping)
    safeRemove(CHECKOUT_KEYS.createdAddressBilling)
  },
}
